import { from, Observable, throwError as observableThrowError } from 'rxjs';

import { catchError, switchMap } from 'rxjs/operators';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { NotificationsService } from 'angular2-notifications';
import { FormCommonDataService } from 'src/sb-modules/Frontend-form-field-module/common/services/form-common-data.service';
import { AuthenticationService } from 'src/sb-tools/typescript/authentication/authentication.service';
import { Error } from 'src/sb-tools/typescript/enums/error.enum';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@environment';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

@Injectable()
export class Interceptor implements HttpInterceptor {
    private token: string;

    constructor(
        private authenticationService: AuthenticationService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        private formCommonDataService: FormCommonDataService,
        private router: Router,
        private location: Location,
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const community = this.formCommonDataService.community || this.authenticationService.community;

        let headers = req.headers.set('Community', community).set('Version', environment.apiVersion);

        if (req.url.toLowerCase().indexOf('oauth/token') === -1 && this.formCommonDataService.isInternal) {
            return from(this.authenticationService.getAuthorizationHeader(community, false, null)).pipe(
                switchMap((data) => {
                    headers = headers.set('Authorization', data.toString());

                    const clonedRequest = req.clone({
                        headers,
                    });

                    // Pass the cloned request instead of the original request to the next handle
                    return next.handle(clonedRequest).pipe(
                        catchError((response) => {
                            return this.handleError(response, clonedRequest);
                        }),
                    );
                }),
            );
        } else {
            const clonedRequest = req.clone({
                headers,
            });

            // Pass the cloned request instead of the original request to the next handle
            return next.handle(clonedRequest).pipe(
                catchError((response) => {
                    return this.handleError(response, clonedRequest);
                }),
            );
        }
    }

    private handleError(response, req) {
        if (this.authenticationService.isLoggingOut) {
            return observableThrowError(response);
        }

        if (req.url.indexOf('oauth/token') !== -1) {
            this.authenticationService.refreshPromise = null;
        }

        let message;
        if (response.status === 400 && req.url.indexOf('oauth/token') !== -1) {
            if (response.error.errorId === Error.sessionExpired) {
                this.authenticationService.logOff({ isNeedRedirect: true });
                return observableThrowError(response);
            }

            if (response.error.errorId === Error.communityWithNameNotFound) {
                this.authenticationService.redirectToCommunities();
                return observableThrowError(response);
            }

            message = response.error['message'] || response.error['error_description'];
            if (message) {
                this.notificationsService.error(this.translateService.instant('Error!'), message);
            }

            this.authenticationService.logOff({ isNeedRedirect: true });
            return observableThrowError(response);
        } else if (response.status === 404) {
            const url = this.location.path();

            this.router.navigate(['404'], {
                skipLocationChange: true,
            });

            this.location.replaceState(url);

            return observableThrowError(response);
        } else {
            if (response.error) {
                message = response.error && response.error.message ? response.error.message : response.error;
                if (message.toLowerCase().indexOf('timeout') >= 0) {
                    message = this.translateService.instant(
                        'Oops... your action was not completed due to a slow Internet connection. Please wait for a few seconds and try again.',
                    );
                }
            } else {
                message = this.translateService.instant(
                    'Oops... Something went wrong, please try again or reload this page',
                );
            }

            this.notificationsService.error(this.translateService.instant('Error!'), message);

            return observableThrowError(response);
        }
    }
}
